import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const UserDetailPanel = ({ user, onUpdate, onClose }) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    phone: user?.phone || '',
    role: user?.role || 'buyer',
    is_verified: user?.is_verified || false,
    rating: user?.rating || 0,
    completed_trips: user?.completed_trips || 0
  });

  const handleSave = async () => {
    await onUpdate(user?.id, formData);
    setEditing(false);
  };

  const handleToggleVerification = async () => {
    await onUpdate(user?.id, { is_verified: !user?.is_verified });
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden">
      {/* Header */}
      <div className="bg-primary/5 px-6 py-4 border-b border-border flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">User Details</h3>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="X" size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {/* Profile Section */}
        <div className="text-center">
          {user?.avatar_url ? (
            <img
              src={user?.avatar_url}
              alt={user?.full_name}
              className="w-24 h-24 rounded-full object-cover mx-auto mb-4"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="User" size={40} className="text-primary" />
            </div>
          )}
          {!editing ? (
            <>
              <h4 className="text-xl font-bold text-foreground mb-1">{user?.full_name}</h4>
              <p className="text-sm text-muted-foreground mb-3">{user?.email}</p>
            </>
          ) : (
            <div className="space-y-3 mb-3">
              <Input
                label="Full Name"
                value={formData?.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e?.target?.value })}
              />
            </div>
          )}
          <div className="flex items-center justify-center gap-2">
            <span
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                user?.role === 'admin' ?'bg-destructive/10 text-destructive'
                  : user?.role === 'traveler' ?'bg-purple-500/10 text-purple-500' :'bg-blue-500/10 text-blue-500'
              }`}
            >
              <Icon
                name={
                  user?.role === 'admin' ?'Shield'
                    : user?.role === 'traveler' ?'Plane' :'ShoppingBag'
                }
                size={14}
              />
              {user?.role}
            </span>
            {user?.is_verified && (
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-success/10 text-success">
                <Icon name="BadgeCheck" size={14} />
                Verified
              </span>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-muted rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Icon name="Star" size={16} className="text-warning fill-warning" />
              <p className="text-2xl font-bold text-foreground">
                {user?.rating?.toFixed(2) || '0.00'}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">Rating</p>
          </div>
          <div className="bg-muted rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Icon name="Package" size={16} className="text-primary" />
              <p className="text-2xl font-bold text-foreground">
                {user?.completed_trips || 0}
              </p>
            </div>
            <p className="text-xs text-muted-foreground">Completed Trips</p>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Phone
            </label>
            {!editing ? (
              <p className="text-sm text-foreground">{user?.phone || 'Not provided'}</p>
            ) : (
              <Input
                value={formData?.phone}
                onChange={(e) => setFormData({ ...formData, phone: e?.target?.value })}
                placeholder="+1234567890"
              />
            )}
          </div>

          {editing && (
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">
                Role
              </label>
              <Select
                value={formData?.role}
                onChange={(e) => setFormData({ ...formData, role: e?.target?.value })}
              >
                <option value="buyer">Buyer</option>
                <option value="traveler">Traveler</option>
                <option value="admin">Admin</option>
              </Select>
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Language
            </label>
            <p className="text-sm text-foreground">{user?.language || 'en'}</p>
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">
              Member Since
            </label>
            <p className="text-sm text-foreground">
              {new Date(user?.created_at)?.toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3 pt-4 border-t border-border">
          {!editing ? (
            <>
              <Button
                variant="default"
                fullWidth
                iconName="Edit"
                onClick={() => setEditing(true)}
              >
                Edit User
              </Button>
              <Button
                variant={user?.is_verified ? 'outline' : 'default'}
                fullWidth
                iconName={user?.is_verified ? 'XCircle' : 'BadgeCheck'}
                onClick={handleToggleVerification}
              >
                {user?.is_verified ? 'Revoke Verification' : 'Verify User'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="default"
                fullWidth
                iconName="Save"
                onClick={handleSave}
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                fullWidth
                onClick={() => {
                  setEditing(false);
                  setFormData({
                    full_name: user?.full_name || '',
                    phone: user?.phone || '',
                    role: user?.role || 'buyer',
                    is_verified: user?.is_verified || false,
                    rating: user?.rating || 0,
                    completed_trips: user?.completed_trips || 0
                  });
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailPanel;